import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Contact, SafetyStatus } from '../types';
import { MessageCircle, Heart, AlertTriangle, CheckCircle, Radio, Navigation, User, X, Zap, Shield, Eye, EyeOff } from 'lucide-react';

interface TacticalHUDProps {
    mode: 'SELF' | 'CONTACT';
    target: Contact;
    onClose: () => void;
    onAction?: (action: string, payload?: any) => void;
    currentStatus?: SafetyStatus;
    position?: { x: number; y: number } | null;
}

const TacticalHUD: React.FC<TacticalHUDProps> = ({ mode, target, onClose, onAction, position }) => {
    // Determine Position (Center by default, or clamp to viewport if tailored)
    // For this design, a centered card is often safest and most "premium", 
    // but if we want it next to the marker, we use the position prop.
    // Let's use a "Smart Center" approach: slightly offset if position is known, but safe.

    // For the "Corrupted" fix, let's strictly Center it to guarantee visibility/safety first.
    // User can just click X to close.
    // We can add a "backdrop" to ensure focus.

    return (
        <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4">
            {/* BACKDROP */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* HOLOGRAPHIC CARD */}
            <AnimatePresence>
                <motion.div
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 20 }}
                    transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                    className="relative w-full max-w-sm bg-black/80 border border-[var(--neon-cyan)]/30 shadow-[0_0_50px_rgba(0,240,255,0.2)] rounded-3xl overflow-hidden backdrop-blur-xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* DECORATIVE HEADER LINE */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[var(--neon-cyan)] to-transparent opacity-50" />

                    {/* --- HEADER SECTION --- */}
                    <div className="relative p-6 flex flex-col items-center border-b border-white/10">
                        {/* AVATAR */}
                        <div className="relative mb-3">
                            <div className="absolute inset-[-4px] rounded-full border border-[var(--neon-cyan)]/50 border-dashed animate-[spin_10s_linear_infinite]" />
                            <div className="w-20 h-20 rounded-full border-2 border-[var(--neon-cyan)] overflow-hidden bg-black shadow-[0_0_20px_rgba(0,240,255,0.3)]">
                                <img src={target.avatarUrl || `https://i.pravatar.cc/300?u=${target.id}`} className="w-full h-full object-cover" />
                            </div>
                            <div className={`absolute bottom-0 right-0 w-5 h-5 rounded-full border-2 border-black flex items-center justify-center ${target.status === 'SAFE' ? 'bg-green-500' : 'bg-red-500'}`}>
                                {target.status === 'SAFE' ? <CheckCircle size={12} className="text-black" /> : <AlertTriangle size={12} className="text-white" />}
                            </div>
                        </div>

                        {/* INFO */}
                        <h2 className="text-xl font-bold text-white tracking-wider">{target.name}</h2>
                        <span className="text-[10px] text-[var(--neon-cyan)] uppercase tracking-[0.2em] opacity-80 mt-1">
                            {mode === 'SELF' ? 'COMMANDER // ONLINE' : 'TACTICAL LINK // ESTABLISHED'}
                        </span>

                        {/* CLOSE BUTTON */}
                        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-white/50 hover:text-white transition-colors">
                            <X size={20} />
                        </button>
                    </div>

                    {/* --- ACTIONS GRID --- */}
                    <div className="p-6">
                        {mode === 'SELF' ? (
                            <div className="flex flex-col gap-6">
                                {/* SELF STATUS */}
                                <div>
                                    <h3 className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wider">Update Status</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <button onClick={() => onAction?.('UPDATE_STATUS', SafetyStatus.SAFE)} className="flex items-center justify-center gap-2 p-3 rounded-xl bg-green-500/10 border border-green-500/30 hover:bg-green-500 hover:text-black hover:border-transparent text-green-500 transition-all group">
                                            <CheckCircle size={18} />
                                            <span className="font-bold text-sm">SAFE</span>
                                        </button>
                                        <button onClick={() => onAction?.('UPDATE_STATUS', SafetyStatus.DANGER)} className="flex items-center justify-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/30 hover:bg-red-500 hover:text-white hover:border-transparent text-red-500 transition-all group">
                                            <AlertTriangle size={18} />
                                            <span className="font-bold text-sm">DANGER</span>
                                        </button>
                                    </div>
                                </div>

                                {/* MOOD BROADCAST */}
                                <div>
                                    <h3 className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wider">Broadcast Status</h3>
                                    <div className="flex justify-between gap-2">
                                        {['👀', '🏠', '🚗', '💤', '🚨'].map(emoji => (
                                            <button
                                                key={emoji}
                                                onClick={() => onAction?.('BROADCAST_MOOD', emoji)}
                                                className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-xl hover:bg-[var(--neon-cyan)] hover:text-black hover:border-transparent transition-all active:scale-95"
                                            >
                                                {emoji}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-3 gap-3">
                                {/* PULSE (Large) */}
                                <button onClick={() => onAction?.('PULSE', target.id)} className="col-span-3 p-4 rounded-xl bg-[var(--neon-cyan)]/10 border border-[var(--neon-cyan)]/50 flex items-center justify-center gap-3 hover:bg-[var(--neon-cyan)] hover:text-black transition-all group shadow-[0_0_15px_rgba(0,240,255,0.1)]">
                                    <Radio className="animate-pulse" size={20} />
                                    <span className="font-bold tracking-widest">SEND PULSE SIGNAL</span>
                                </button>

                                {/* CHAT */}
                                <button onClick={() => onAction?.('CHAT', target.id)} className="p-3 rounded-xl bg-white/5 border border-white/10 flex flex-col items-center justify-center gap-2 hover:bg-white hover:text-black transition-all h-24">
                                    <MessageCircle size={20} />
                                    <span className="text-[10px] font-bold tracking-wider">CHAT</span>
                                </button>

                                {/* TRACK */}
                                <button onClick={() => onAction?.('TRACK', target.id)} className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/30 flex flex-col items-center justify-center gap-2 hover:bg-blue-500 hover:text-white transition-all h-24">
                                    <Navigation size={20} />
                                    <span className="text-[10px] font-bold tracking-wider">TRACK</span>
                                </button>

                                {/* PROFILE */}
                                <button onClick={() => onAction?.('PROFILE', target.id)} className="p-3 rounded-xl bg-purple-500/10 border border-purple-500/30 flex flex-col items-center justify-center gap-2 hover:bg-purple-500 hover:text-white transition-all h-24">
                                    <User size={20} />
                                    <span className="text-[10px] font-bold tracking-wider">PROFILE</span>
                                </button>

                                {/* LOVE */}
                                <button onClick={() => onAction?.('LOVE', target.id)} className="col-span-1.5 p-3 rounded-xl bg-pink-500/10 border border-pink-500/30 flex items-center justify-center gap-2 hover:bg-pink-500 hover:text-white transition-all">
                                    <Heart size={16} />
                                    <span className="text-[10px] font-bold">LOVE</span>
                                </button>

                                {/* WARN */}
                                <button onClick={() => onAction?.('WARN', target.id)} className="col-span-1.5 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center gap-2 hover:bg-yellow-500 hover:text-black transition-all">
                                    <AlertTriangle size={16} />
                                    <span className="text-[10px] font-bold">WARN</span>
                                </button>
                            </div>
                        )}
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default TacticalHUD;
